/**
 * The shared filter state (PRD 7.2), and its round trip through the URL.
 *
 * Every view reads the same object, so a filter set on the dashboard survives a jump to the
 * heatmap, and every screen is a link: the query string is the whole state. Only values that
 * differ from the defaults are written, so the common case is a bare path and a shared link
 * never carries a filter the sender did not set.
 *
 * Pure: no DOM, no history. `URLSearchParams` is the only platform API, and Node has it too, so
 * the same code runs in the tests and (later) in the digest and export builders.
 */

/** Planning grain (PRD 7.2). Daily for the near term, weekly beyond; the views zoom, the data does not. */
export const GRAINS = Object.freeze(['day', 'week', 'month', 'quarter']);

/** The dimension a view groups by, matching the forecast reads' `dimension`. */
export const DIMENSIONS = Object.freeze([
  'company',
  'department',
  'service',
  'office',
  'individual',
  'project',
]);

/** Record status (PRD 7.2). */
export const STATUSES = Object.freeze(['open', 'scheduled', 'opportunity', 'closed']);

/** Confidence tiers (PRD 6.1). */
export const TIERS = Object.freeze(['high', 'medium', 'low']);

/** Opportunity mode (PRD 7.2, TRD 8.8). `sampled` is the third mode, built in Phase 39. */
export const OPPORTUNITY_MODES = Object.freeze(['weighted', 'unweighted', 'sampled', 'none']);

/** The modes an allocation row can be filtered to (`pipeline=` on the Phase 13 endpoints). */
export const PIPELINE_READ_MODES = Object.freeze(['weighted', 'unweighted', 'none']);

/**
 * The mode a row-based read is taken in, for a given opportunity mode.
 *
 * Four modes go in and three come out, because `sampled` is not a set of rows. A run writes its
 * pipeline hours twice, weighted and unweighted, and the sampled mode is a distribution over
 * those same hours (`forecast_samples`, read through `GET /forecast/samples`) — there is nothing
 * for a ribbon or a heatmap to stack that is sampled. Its central line, though, is the weighted
 * one: the mean of the draws is hours times probability, which is what the weighted rows hold.
 * So a reader who has chosen Sampled sees the weighted stack with the band drawn over it where a
 * view can draw one, rather than a view that empties or one that quietly falls back to whatever
 * the run's stored default happens to be.
 * @param {string} mode
 */
export function pipelineReadMode(mode) {
  if (mode === 'sampled') return 'weighted';
  return PIPELINE_READ_MODES.includes(mode) ? mode : null;
}

/**
 * The default state. A key at its default is left out of the URL.
 * `from` and `run` are null meaning "the run decides": the horizon start and the latest run.
 */
export const DEFAULTS = Object.freeze({
  from: null,
  weeks: 13,
  grain: 'week',
  dimension: 'company',
  entity: null,
  types: Object.freeze([]),
  services: Object.freeze([]),
  departments: Object.freeze([]),
  offices: Object.freeze([]),
  people: Object.freeze([]),
  statuses: Object.freeze([]),
  tiers: Object.freeze([]),
  mode: 'weighted',
  run: null,
  compare: null,
  // The scenario every view is read through (PRD 7.5; Phase 35). Null is the base forecast.
  // It travels in the URL like any other filter, so a scenario is a link a planner can send.
  scenario: null,
});

/** URL key per state key. Short, because these end up in shared links. */
const KEYS = Object.freeze({
  from: 'from',
  weeks: 'weeks',
  grain: 'grain',
  dimension: 'dim',
  entity: 'entity',
  types: 'types',
  services: 'services',
  departments: 'depts',
  offices: 'offices',
  people: 'people',
  statuses: 'status',
  tiers: 'tiers',
  mode: 'mode',
  run: 'run',
  compare: 'compare',
  scenario: 'scenario',
});

const LIST_KEYS = Object.freeze([
  'types',
  'services',
  'departments',
  'offices',
  'people',
  'statuses',
  'tiers',
]);

const ENUMS = Object.freeze({
  grain: GRAINS,
  dimension: DIMENSIONS,
  mode: OPPORTUNITY_MODES,
  statuses: STATUSES,
  tiers: TIERS,
});

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MIN_WEEKS = 1;
const MAX_WEEKS = 104;

/** A fresh copy of the defaults (the frozen lists would otherwise leak into state). */
export function defaultFilters() {
  return { ...DEFAULTS, ...Object.fromEntries(LIST_KEYS.map((k) => [k, []])) };
}

const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort();

/**
 * Read filter state out of a query string. Anything unrecognised is dropped rather than carried,
 * so a hand-edited or stale link degrades to the defaults instead of filtering to nothing.
 * @param {string | URLSearchParams} search
 */
export function parseFilters(search) {
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(search ?? '');
  const state = defaultFilters();

  for (const key of LIST_KEYS) {
    const raw = params.get(KEYS[key]);
    if (raw === null) continue;
    let values = uniqueSorted(raw.split(',').map((v) => v.trim()));
    if (ENUMS[key]) values = values.filter((v) => ENUMS[key].includes(v));
    state[key] = values;
  }

  for (const key of ['grain', 'dimension', 'mode']) {
    const raw = params.get(KEYS[key]);
    if (raw !== null && ENUMS[key].includes(raw)) state[key] = raw;
  }

  const from = params.get(KEYS.from);
  if (from !== null && ISO_DATE.test(from)) state.from = from;

  const weeks = params.get(KEYS.weeks);
  if (weeks !== null) {
    const n = Number(weeks);
    if (Number.isInteger(n) && n >= MIN_WEEKS && n <= MAX_WEEKS) state.weeks = n;
  }

  for (const key of ['entity', 'run', 'compare', 'scenario']) {
    const raw = params.get(KEYS[key]);
    if (raw) state[key] = raw;
  }

  // An entity without a dimension to hang on is not a filter, it is a typo.
  if (state.entity && state.dimension === 'company') state.entity = null;

  return state;
}

const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Write filter state as a query string, defaults omitted. Keys come out in a fixed order so the
 * same state always produces the same URL (and the same history entry).
 * @param {ReturnType<typeof defaultFilters>} state
 */
export function filtersToSearch(state) {
  const params = new URLSearchParams();
  for (const key of Object.keys(KEYS)) {
    const value = state?.[key];
    if (value === undefined || value === null) continue;
    if (LIST_KEYS.includes(key)) {
      const list = uniqueSorted(value);
      if (list.length && !sameList(list, [...DEFAULTS[key]])) params.set(KEYS[key], list.join(','));
      continue;
    }
    if (value === DEFAULTS[key] || value === '') continue;
    params.set(KEYS[key], String(value));
  }
  return params.toString();
}

/** `?a=b` (or '' when nothing is set), ready to concatenate onto a path. */
export function filtersToQuery(state) {
  const search = filtersToSearch(state);
  return search ? `?${search}` : '';
}

/**
 * Apply a patch, normalising as `parseFilters` would. Unknown enum values are ignored, so a
 * control cannot push the state somewhere a URL could not.
 * @param {ReturnType<typeof defaultFilters>} state
 * @param {Partial<ReturnType<typeof defaultFilters>>} patch
 */
export function withFilters(state, patch) {
  const next = { ...state };
  for (const [key, value] of Object.entries(patch ?? {})) {
    if (!(key in KEYS)) continue;
    if (LIST_KEYS.includes(key)) {
      let list = uniqueSorted(Array.isArray(value) ? value : value ? [value] : []);
      if (ENUMS[key]) list = list.filter((v) => ENUMS[key].includes(v));
      next[key] = list;
      continue;
    }
    if (ENUMS[key] && value !== null && !ENUMS[key].includes(value)) continue;
    next[key] = value === '' ? null : value;
  }
  if (next.dimension === 'company') next.entity = null;
  return next;
}

/** Toggle one value of a multi-select filter. */
export function toggleFilter(state, key, value) {
  if (!LIST_KEYS.includes(key)) return state;
  const current = state[key] ?? [];
  const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
  return withFilters(state, { [key]: next });
}

/** Whether anything is set beyond the defaults — drives the "Clear filters" affordance. */
export function isDefaultFilters(state) {
  return filtersToSearch(state) === '';
}

/**
 * The run every read is taken from: the scenario's, when one is selected, else the run the
 * reader pinned, else nothing (the endpoint's own default, the latest base run).
 *
 * `scenario:<id>` is a run reference the API resolves to that scenario's newest succeeded run
 * (`getRun`), which is why selecting a scenario moves every view at once and no view has any
 * scenario code of its own. A scenario wins over a pinned run: a reader who has chosen both has
 * asked for a scenario, and the run picker is disabled while one is selected.
 * @param {ReturnType<typeof defaultFilters>} state
 */
export function runReference(state) {
  if (state?.scenario) return `scenario:${state.scenario}`;
  return state?.run ?? null;
}

/**
 * The query the forecast reads want: the API's own parameter names, and only the ones a given
 * endpoint understands. Views pass the keys they support. `entity` travels only with a
 * dimension that can take one (the state never holds an entity under `company`).
 * @param {ReturnType<typeof defaultFilters>} state
 * @param {string[]} keys
 */
export function apiParams(state, keys) {
  const all = {
    run_id: runReference(state),
    from: state.from,
    weeks: state.weeks,
    dimension: state.dimension,
    entity: state.entity,
    // `sampled` reads as weighted here and as its own endpoint elsewhere: see `pipelineReadMode`.
    pipeline: pipelineReadMode(state.mode),
    compare_run_id: state.compare,
  };
  const out = {};
  for (const key of keys) {
    const value = all[key];
    if (value !== null && value !== undefined) out[key] = value;
  }
  return out;
}
